import React, { lazy, Suspense, useEffect } from "react";

import ReactGA from "react-ga";
import CacheBuster from 'react-cache-buster';
import { Switch, Route, Redirect } from "react-router-dom";
import { ConnectedRouter } from 'connected-react-router';
import { GoogleOAuthProvider } from '@react-oauth/google';

import CurriculumLoader from "./components/CurriculumLoader";
import checkActivity from './activity';
import history from './history';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import Loader from './components/MainLoader';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import NavBar from './components/TopMenu/NavBar.js';
import AppLoader from "./containers/AppLoader";
import Notification from './containers/NotificationService/Notification';
import { NotificationProvider } from './shared/components/Notifications';
import { TEXTJAM_HEADER_HEIGHT } from './modules/ide/textjam';
import UserwayAccessibilityWidget from './components/userwayAccessibilityWidget/userwayAccessibilityWidget.js';
import { logout } from './activity';

import useDeviceDetect from "./utils/deviceDetect";
import { checkIsClubsDomain } from './utils/generic';
import * as tokenStorage from './tokenStorage';
import config from '../package.json';

// Route-level code splitting: each screen and its dependencies load on demand.
const Home = lazy(() => import("./containers/Home/home"));
const Curriculum = lazy(() => import("./containers/Curriculum"));
const LoginSignUp = lazy(() => import("./containers/LoginSignUp"));
const JoinClub = lazy(() => import("./containers/JoinProgram/joinClub"));
const GettingStarted = lazy(() => import("./pages/getting-started/gettingStarted"));
const StaticPages = lazy(() => import("./pages/static/staticPages"));
const ProjectGallery = lazy(() => import("./pages/project-gallery/project-gallery"));
const Project = lazy(() => import("./pages/project/project"));
const UserProfile = lazy(() => import("./pages/user-profile/user-profile"));
const RenewClub = lazy(() => import("./pages/renew-club/renew-club"));
const ClubProfile = lazy(() => import("./containers/ClubProfile"));
const PublicProfile = lazy(() => import("./pages/public-profile/public-profile"));
const Announcement = lazy(() => import("./containers/Announcement/Announcement"));
const AnnouncementView = lazy(() => import("./containers/Announcement/AnnouncementView"));
const ForgotPassword = lazy(() => import("./containers/ForgotPassword"));
const ChangePassword = lazy(() => import("./modules/auth/reset-password/ui/ChangePasswordPage"));
const CommunityPartnerhip = lazy(() => import("./containers/CommunityPartnership/communityPartnership"));
const CommunityPartnerhipResources = lazy(() => import("./containers/CommunityPartnership/CommunityPartnershipContainer/CommunityPartnerhipResources"));
const SubmitProjectGallery = lazy(() => import("./containers/SubmitProjectGallery"));
const Apply = lazy(() => import("./containers/ApplyPage"));
const StartClub = lazy(() => import("./containers/ApplyPage/startClub/apply.page"));
const Attendance = lazy(() => import("./containers/Attendance/attendance.page"));
const SetPassword = lazy(() => import("./modules/auth/reset-password/ui/SetPasswordPage"));
const Members = lazy(() => import("./containers/Members"));
const Progress = lazy(() => import("./containers/Progress"));
const Calendar = lazy(() => import("./containers/Calendar"));
const UserProgress = lazy(() => import("./containers/UserProgress"));
const LandingPage = lazy(() => import("./containers/LandingPage"));
const VerifyCertificate = lazy(() => import("./containers/VerifyCertificate"));
const GWCChallenge = lazy(() => import("./containers/GWCChallenge"));
const BlockJam = lazy(() => import("./containers/IDE/BlockJam/index.js"));
const TextJamSharedProject = lazy(() => import("./containers/IDE/TextJam/SharedTextJam.js"));
const BlockJamPlayground = lazy(() => import("./containers/IDE/BlockJam/BlockJamPlayground.js"));
const MyProjects = lazy(() => import("./containers/MyProjects"));
const TextJamPlayground = lazy(() => import("./containers/IDE/TextJam/TextJamPlayground.js"));
const TextJamCurriculum = lazy(() => import("./containers/IDE/TextJam/index.js"));
const BlockJamSharedProject = lazy(() => import("./containers/IDE/BlockJam/SharedBlockJam.js"));

const clearSession = () => {
  alert('Session was cleared');
  logout();
};

const TRACKING_ID = window._env_.REACT_APP_GOOGLE_ANALYTIC_ID;

function App() {
  const isMobile = useDeviceDetect();
  const isClubsDomain = checkIsClubsDomain();

  history.listen((location) => {
    if (!!TRACKING_ID) {
      ReactGA.initialize(TRACKING_ID, {
        titleCase: false,
        gaOptions: {
          siteSpeedSampleRate: 100,
        },
      });
      ReactGA.set({ page: location.pathname });
      ReactGA.pageview(location.pathname);
    }

    checkActivity();
  });

  useEffect(() => {
    checkActivity();
  }, []);

  const token = tokenStorage.getToken();
  const { pathname, search } = window.location;
  const redirectPath = !!pathname && typeof pathname === 'string' && !pathname.includes('/home');
  const landingUrl = redirectPath ? `/${search}`: '/';
  const userwayAccessibilityWidgetApiKey = window._env_.REACT_APP_USERWAY_ACCESSIBILITY_WIDGET_API_KEY

  return (
    <CacheBuster
      currentVersion={config.version}
      isVerboseMode={false}
      isEnabled={true}
    >
      <div className="App">
        {!!userwayAccessibilityWidgetApiKey && <UserwayAccessibilityWidget apiKey={userwayAccessibilityWidgetApiKey} />}
        <GoogleOAuthProvider clientId={window._env_.REACT_APP_GOOGLE_CLIENT_ID}>
          <NotificationProvider topOffset={TEXTJAM_HEADER_HEIGHT}>
            <ConnectedRouter history={history}>
              <RouteErrorBoundary>
              <AppLoader>
                <NavBar isMobile={isMobile} />
                <Notification/>
                <CurriculumLoader>
                  <div id="maincontent"></div>
                  <RouteErrorBoundary>
                    <Suspense fallback={<Loader />}>
                    <Switch>
                    <Route
                      component={clearSession}
                      exact
                      path={["/clear-session", "/logout"]}
                    />
                    <Route path="/login" exact component={LoginSignUp} />
                    <Route path="/" exact component={LandingPage} />
                    <Route path={["/facilitatorInvitation/:facilitatorInvitationToken", "/invitation/:invitationToken"]} exact component={LoginSignUp} />
                    <Route path="/community-partner-dashboard-faq" exact component={GettingStarted}></Route>
                    <Route path="/international-community-partner-dashboard-faq" exact component={GettingStarted}></Route>
                    <Route path="/terms" exact component={GettingStarted}></Route>
                    <Route path="/privacy" exact component={GettingStarted}></Route>
                    <Route path="/cookie-policy" exact component={GettingStarted}></Route>
                    <Route path="/static-pages/:page" component={StaticPages}></Route>
                    <Route path="/code-of-conduct" exact component={GettingStarted}></Route>
                    <Route path="/forgot-password" exact component={ForgotPassword}></Route>
                    <Route path="/set-password/:token" exact component={SetPassword}></Route>
                    <Route path="/change-password/:token" exact component={ChangePassword}></Route>
                    <Route path="/verify/:token" exact component={VerifyCertificate}></Route>
                    <Route path="/gwc-challenges" exact component={GWCChallenge}></Route>

                    {!isClubsDomain ? <Route path="/project-gallery/:projectId" component={Project}></Route> :
                      <AuthenticatedRoute path="/project-gallery/:projectId" component={Project}></AuthenticatedRoute>}
                    {!isClubsDomain ? <Route path="/project-gallery" component={ProjectGallery}></Route> :
                      <AuthenticatedRoute path="/project-gallery" component={ProjectGallery}></AuthenticatedRoute>}

                    <AuthenticatedRoute path="/attendance" exact component={Attendance}/>
                    <AuthenticatedRoute path="/messages" exact component={Announcement}/>
                    <AuthenticatedRoute path={["/loop/:programCode/post/:postId", "/post/:postId"]} exact component={AnnouncementView}/>
                    <AuthenticatedRoute path={["/join-program", "/join-program/:childId", "/join-program/clubCode/:clubCode", "/join-club/:invitationToken","/join-program/qrCodeInvitation/:qrClubCode" ]} exact component={JoinClub} />
                    <AuthenticatedRoute path="/join-loop" exact component={JoinClub} />
                    <AuthenticatedRoute path="/user-profile" exact component={UserProfile} />
                    <AuthenticatedRoute path="/profile/:userId" exact component={PublicProfile} />
                    <AuthenticatedRoute path="/club-profile/:programCode" component={ClubProfile} />
                    <AuthenticatedRoute path="/new-project" exact component={SubmitProjectGallery} />
                    <AuthenticatedRoute path="/home" exact component={Home}/>
                    <AuthenticatedRoute path="/gwc-club-application" exact component={Apply}/>
                    <AuthenticatedRoute path="/start-club-application" exact component={StartClub}/>
                    <AuthenticatedRoute path="/members/:clubCode" exact component={Members}/>
                    <AuthenticatedRoute path="/calendar" exact component={Calendar}/>
                    <AuthenticatedRoute path="/renew-club/:code" exact component={RenewClub}/>

                    <AuthenticatedRoute path="/gwc" component={Curriculum} />
                    <AuthenticatedRoute path="/community-partnership/resources/community-partner-resources" component={CommunityPartnerhipResources}/>
                    <AuthenticatedRoute path="/community-partnership" exact component={CommunityPartnerhip}/>
                    <AuthenticatedRoute path={["/progress/:clubCode",
                      "/progress/:clubCode/view/:tab/:id?/:progressInputParam?",
                      "/progress/:clubCode/user/:userId/view/:tab?/:id?",
                      "/progress/:clubCode/user/:userId/"]}
                      exact component={Progress}/>
                    <AuthenticatedRoute path="/progress/:clubCode/:userId/:tab?/:id?" exact component={UserProgress}/>
                    <AuthenticatedRoute path="/BlockJam" exact component={BlockJam}></AuthenticatedRoute>
                    <AuthenticatedRoute path="/BlockJam/playground/:repositoryId?" exact component={BlockJamPlayground}></AuthenticatedRoute>
                    <AuthenticatedRoute path="/my-projects" exact component={MyProjects}></AuthenticatedRoute>
                    <Route exact path="/BlockJam/gwc/:programId/:repositoryId/:hash/:publicHash" component={BlockJamSharedProject} />
                    <AuthenticatedRoute exact path="/BlockJam/:programId/:repositoryId/:hash" component={BlockJamSharedProject} />
                    <Route exact path="/BlockJam/gwc/:repositoryId/:hash/:publicHash" component={BlockJamSharedProject} />
                    <AuthenticatedRoute exact path="/BlockJam/:repositoryId/:hash" component={BlockJamSharedProject} />
                    <AuthenticatedRoute exact path="/TextJam/:playgroundType/curriculum/:programId(\d+)/set/:activitySetId/:repositoryId(\d+)" component={TextJamCurriculum} />
                    <AuthenticatedRoute exact path="/TextJam/:playgroundType/curriculum/:programId(\d+)/set/:activitySetId" component={TextJamCurriculum} />
                    <AuthenticatedRoute exact path="/TextJam/:playgroundType/:repositoryId(\d+)?" component={TextJamPlayground} />
                    <AuthenticatedRoute exact path="/TextJam/:playgroundType/:repositoryId(\d+)/:hash" component={TextJamSharedProject} />
                    <Route exact path="/TextJam/gwc/:playgroundType/:repositoryId/:hash/:publicHash" component={TextJamSharedProject} />

                    {/* Redirect if none of the routes matches */}
                    <Route render={() => <Redirect to={!!token ? "/home" : landingUrl} />} />
                    </Switch>
                    </Suspense>
                  </RouteErrorBoundary>
                </CurriculumLoader>
              </AppLoader>
              </RouteErrorBoundary>
            </ConnectedRouter>
          </NotificationProvider>
        </GoogleOAuthProvider>
      </div>
    </CacheBuster>
  );
}

export default App;
